import { useMemo, useState } from 'react';
import {
  Plug,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ShieldCheck,
  KeyRound,
  ListChecks,
  Rocket,
  ArrowLeft,
  ArrowRight,
  Check,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  cn,
  type SelectOption,
} from '../../ui';
import { useOnboarding } from '../../features/onboarding/useOnboarding';
import {
  VECTOR_ROLE_NAME,
  roleArnForAccount,
  type AccountDraft,
} from '../../features/onboarding/types';

const STAGE_OPTIONS: SelectOption[] = [
  { value: 'beta', label: 'beta' },
  { value: 'gamma', label: 'gamma' },
  { value: 'prod', label: 'prod' },
];

const STEPS = [
  { title: 'Name your team', hint: 'Identify the team and its Bindle.' },
  { title: 'Grant an account', hint: 'Add the AWS account(s) to onboard.' },
  { title: 'Start & status', hint: 'Kick off onboarding and watch progress.' },
] as const;

/**
 * Onboarding wizard — a team connects its account(s) / knowledge base to Vector
 * by granting a cross-account IAM role per account. The page reaches the backend
 * ONLY through the useOnboarding hook (which wraps api.teamOnboarding).
 *
 * Presented as a 3-step stepper (Name your team → Grant an account → Start &
 * status) to lower first-run cognitive load; the backend contract is unchanged.
 * In step 1 the Bindle picker comes before the Bindle id field, which reads
 * back the selection.
 */
export function OnboardingPage() {
  const o = useOnboarding();
  const [step, setStep] = useState(0);
  const [bindleFilter, setBindleFilter] = useState('');
  const [bindlePickerOpen, setBindlePickerOpen] = useState(false);
  const bindleSearchTerm = bindleFilter.trim().toLowerCase();
  const filteredBindles = (o.bindles ?? []).filter(
    (b) =>
      bindleSearchTerm.length === 0 ||
      (b.bindleName ?? '').toLowerCase().includes(bindleSearchTerm) ||
      b.bindleId.toLowerCase().includes(bindleSearchTerm),
  );

  const lastStep = STEPS.length - 1;
  const goNext = () => setStep((s) => Math.min(s + 1, lastStep));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div>
      <PageHeader
        icon={<Plug className="h-5 w-5" />}
        title="Team Onboarding"
        description="Connect your team's account and knowledge base to Vector."
      />

      {/* Stepper */}
      <ol className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-2">
        {STEPS.map((s, i) => {
          const state =
            i < step ? 'done' : i === step ? 'current' : 'upcoming';
          return (
            <li key={s.title} className="flex-1">
              <button
                type="button"
                onClick={() => setStep(i)}
                aria-current={state === 'current' ? 'step' : undefined}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                  state === 'current'
                    ? 'border-accent/60 bg-surface-2'
                    : 'border-surface-border bg-surface-1 hover:bg-surface-2',
                )}
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    state === 'done'
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : state === 'current'
                        ? 'bg-accent-soft text-accent-fg'
                        : 'bg-surface-3 text-zinc-500',
                  )}
                >
                  {state === 'done' ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-zinc-100">
                    {s.title}
                  </span>
                  <span className="block truncate text-xs text-zinc-500">
                    {s.hint}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Step 1: Name your team */}
      {step === 0 && (
        <div className="space-y-6">
          {/* Intro / explainer (collapsed by default to reduce jargon) */}
          <details className="rounded-2xl border border-surface-border bg-surface-2">
            <summary className="flex cursor-pointer items-center gap-3 px-5 py-4 text-sm text-zinc-300">
              <ShieldCheck
                className="h-5 w-5 shrink-0 text-accent"
                aria-hidden="true"
              />
              How does Vector connect to my account? (cross-account IAM role)
            </summary>
            <div className="space-y-2 border-t border-surface-border px-5 py-4 text-sm text-zinc-400">
              <p>
                Vector is <span className="text-zinc-200">multi-tenant</span>: to
                connect your team's AWS account and knowledge base, you grant
                Vector a{' '}
                <span className="text-zinc-200">cross-account IAM role</span>{' '}
                that it assumes to read your resources. You create that role in
                each account you want to onboard, then validate and start
                onboarding.
              </p>
              <p>
                Each granted role must follow the required naming convention{' '}
                <code className="rounded bg-surface-1 px-1.5 py-0.5 text-xs text-accent-fg">
                  {VECTOR_ROLE_NAME}
                </code>{' '}
                and trust Vector's service principal for{' '}
                <code className="rounded bg-surface-1 px-1.5 py-0.5 text-xs text-accent-fg">
                  sts:AssumeRole
                </code>
                .
              </p>
            </div>
          </details>

          <Card>
            <CardHeader>
              <CardTitle>Team</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <Input
                label="Team name"
                placeholder="e.g. Engineering Vector"
                value={o.teamName}
                onChange={(e) => o.setTeamName(e.target.value)}
              />

              {/* Bindle picker: find the Bindle first, then read it back below. */}
              <div className="rounded-xl border border-surface-border bg-surface-1/60 p-4">
                <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                  <Input
                    label="Team id"
                    placeholder="amzn1.abacus.team.xxxxxxxx"
                    value={o.teamId}
                    onChange={(e) => o.setTeamId(e.target.value)}
                    hint="Your team's abacus id; lists the bindles it owns so you can pick your Bindle."
                  />
                  <Button
                    variant="secondary"
                    leftIcon={<ListChecks className="h-4 w-4" />}
                    loading={o.bindlesLoading}
                    disabled={!o.canListBindles}
                    onClick={() => {
                      setBindlePickerOpen(true);
                      void o.listBindles();
                    }}
                  >
                    List my bindles
                  </Button>
                </div>

                {bindlePickerOpen &&
                  (o.bindlesError ? (
                    <p className="mt-3 text-sm text-red-400" role="alert">
                      {o.bindlesError}
                    </p>
                  ) : o.bindlesLoading ? (
                    <div className="mt-3 flex items-center justify-center py-4">
                      <Spinner label="Loading bindles" />
                    </div>
                  ) : o.bindles ? (
                    o.bindles.length === 0 ? (
                      <p className="mt-3 text-sm text-zinc-500">
                        No bindles are owned by that team id.
                      </p>
                    ) : (
                      <div className="mt-3 space-y-2">
                        <Input
                          placeholder="Search bindles by name or id"
                          value={bindleFilter}
                          onChange={(e) => setBindleFilter(e.target.value)}
                          aria-label="Search bindles"
                        />
                        {filteredBindles.length === 0 ? (
                          <p className="text-sm text-zinc-500">
                            No bindles match your search.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {filteredBindles.map((b) => {
                              const selected = o.bindleId === b.bindleId;
                              return (
                                <li key={b.bindleId}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      o.selectBindle(b.bindleId);
                                      setBindlePickerOpen(false);
                                    }}
                                    aria-pressed={selected}
                                    className="group flex w-full items-center gap-3 rounded-xl border border-surface-border bg-surface-1 px-3 py-2 text-left transition-colors hover:border-accent/60 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent aria-pressed:border-accent aria-pressed:bg-surface-2"
                                  >
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-sm font-medium text-zinc-100">
                                        {b.bindleName || b.bindleId}
                                      </span>
                                      <span className="mt-0.5 block truncate font-mono text-xs text-zinc-500">
                                        {b.bindleId}
                                      </span>
                                    </span>
                                    {selected && (
                                      <CheckCircle2
                                        className="h-4 w-4 shrink-0 text-emerald-300"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )
                  ) : null)}
              </div>

              <Input
                label="Bindle id"
                placeholder="e.g. amzn1.bindle.resource.xxxxxxxx"
                value={o.bindleId}
                onChange={(e) => o.setBindleId(e.target.value)}
                hint="Auto-filled when you pick a bindle above; also used to look up status. You can paste one directly."
              />
            </CardBody>
          </Card>
        </div>
      )}

      {/* Step 2: Grant an account */}
      {step === 1 && (
        <Card>
          <CardHeader className="items-center">
            <CardTitle>Accounts to grant</CardTitle>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={o.addAccount}
            >
              Add account
            </Button>
          </CardHeader>
          <CardBody className="space-y-4">
            {o.accounts.map((acct, idx) => (
              <AccountRow
                key={acct.id}
                index={idx}
                draft={acct}
                canRemove={o.accounts.length > 1}
                onChange={(patch) => o.updateAccount(acct.id, patch)}
                onRemove={() => o.removeAccount(acct.id)}
                onValidate={() => void o.validateAccount(acct.id)}
              />
            ))}
          </CardBody>
        </Card>
      )}

      {/* Step 3: Start & status */}
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-zinc-400">
                Starts onboarding for the entered Bindle id with every account
                that has all fields filled.
              </div>
              <Button
                leftIcon={<Rocket className="h-4 w-4" />}
                loading={o.starting}
                disabled={!o.canStart}
                onClick={() => void o.start()}
              >
                Start onboarding
              </Button>
            </CardBody>
            {(o.startError || o.startResult) && (
              <CardBody className="border-t border-surface-border">
                {o.startError && (
                  <p className="text-sm text-red-400" role="alert">
                    {o.startError}
                  </p>
                )}
                {o.startResult && (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">Status:</span>
                      <Badge tone="info">{o.startResult.status}</Badge>
                    </div>
                    {o.startResult.executionArn && (
                      <div className="break-all text-xs text-zinc-500">
                        <span className="text-zinc-400">Execution ARN: </span>
                        <code className="text-zinc-300">
                          {o.startResult.executionArn}
                        </code>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            )}
          </Card>

          <Card>
            <CardHeader className="items-center">
              <CardTitle>Onboarding status</CardTitle>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RefreshCw className="h-4 w-4" />}
                loading={o.statusLoading}
                onClick={() => void o.refreshStatus()}
              >
                Check status
              </Button>
            </CardHeader>
            <CardBody>
              {o.statusError ? (
                <p className="text-sm text-red-400" role="alert">
                  {o.statusError}
                </p>
              ) : o.statusLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner label="Loading status" />
                </div>
              ) : o.status ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-zinc-500">Status:</span>
                    <Badge tone="info">{o.status.status}</Badge>
                  </div>
                  {o.status.accounts.length === 0 ? (
                    <p className="text-sm text-zinc-500">
                      No accounts recorded for this Bindle yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {o.status.accounts.map((a, i) => (
                        <li
                          key={`${a.accountId}-${i}`}
                          className="flex flex-wrap items-center gap-2 rounded-xl border border-surface-border bg-surface-1 px-3 py-2 text-sm"
                        >
                          <span className="font-mono text-zinc-200">
                            {a.accountId || '—'}
                          </span>
                          {a.stage && <Badge tone="neutral">{a.stage}</Badge>}
                          {a.region && (
                            <span className="text-xs text-zinc-500">
                              {a.region}
                            </span>
                          )}
                          <span className="ml-auto flex items-center gap-2">
                            <Badge tone={a.valid ? 'success' : 'danger'} dot>
                              {a.valid ? 'valid' : 'invalid'}
                            </Badge>
                            <Badge
                              tone={a.onboarded ? 'success' : 'warning'}
                              dot
                            >
                              {a.onboarded ? 'onboarded' : 'pending'}
                            </Badge>
                          </span>
                          {a.errorMessage && (
                            <p className="w-full text-xs text-red-400">
                              {a.errorMessage}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={<Plug className="h-6 w-6" />}
                  title="No status loaded"
                  description="Enter your Bindle id above and choose Check status to view the recorded onboarding state."
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Step navigation */}
      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={goBack}
          disabled={step === 0}
        >
          Back
        </Button>
        {step < lastStep && (
          <Button
            rightIcon={<ArrowRight className="h-4 w-4" />}
            onClick={goNext}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
}

interface AccountRowProps {
  index: number;
  draft: AccountDraft;
  canRemove: boolean;
  onChange: (patch: Partial<AccountDraft>) => void;
  onRemove: () => void;
  onValidate: () => void;
}

/** One editable account grant row with the role convention + validation. */
function AccountRow({
  index,
  draft,
  canRemove,
  onChange,
  onRemove,
  onValidate,
}: AccountRowProps) {
  const expectedArn = useMemo(
    () => roleArnForAccount(draft.accountId),
    [draft.accountId],
  );
  const canValidate =
    draft.accountId.trim().length > 0 &&
    draft.roleArn.trim().length > 0 &&
    draft.validation !== 'validating';

  return (
    <div className="rounded-xl border border-surface-border bg-surface-1/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Account {index + 1}
        </h4>
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove account ${index + 1}`}
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={onRemove}
          >
            Remove
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Account ID"
          placeholder="123456789012"
          inputMode="numeric"
          value={draft.accountId}
          onChange={(e) => onChange({ accountId: e.target.value })}
        />
        <Select
          label="Stage"
          options={STAGE_OPTIONS}
          value={draft.stage}
          onChange={(e) =>
            onChange({ stage: e.target.value as AccountDraft['stage'] })
          }
        />
        <Input
          label="Region"
          placeholder="us-east-1"
          value={draft.region}
          onChange={(e) => onChange({ region: e.target.value })}
        />
        <Input
          label="Role ARN"
          value={draft.roleArn}
          onChange={(e) =>
            onChange({ roleArn: e.target.value, roleArnAuto: false })
          }
        />
      </div>

      {/* Required convention + what-to-grant guidance */}
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent-soft/20 px-3 py-2">
        <KeyRound
          className="mt-0.5 h-4 w-4 shrink-0 text-accent"
          aria-hidden="true"
        />
        <div className="space-y-1 text-xs text-zinc-400">
          <p>
            <span className="text-zinc-300">Required role ARN:</span>{' '}
            <code className="break-all text-accent-fg">{expectedArn}</code>
          </p>
          <p>
            Create this role in the account, name it exactly{' '}
            <code className="text-accent-fg">{VECTOR_ROLE_NAME}</code>, and grant
            Vector's service principal{' '}
            <code className="text-accent-fg">sts:AssumeRole</code> in its trust
            policy.
          </p>
        </div>
      </div>

      {/* Validate action + result */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          loading={draft.validation === 'validating'}
          disabled={!canValidate}
          onClick={onValidate}
        >
          Validate
        </Button>
        {draft.validation === 'valid' && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Valid
          </span>
        )}
        {draft.validation === 'invalid' && (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-red-400"
            role="alert"
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Invalid{draft.errorMessage ? ` — ${draft.errorMessage}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

export default OnboardingPage;
